import React, { useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ScheduleDateTimePicker.css';

export function defaultScheduleDate(initialDate) {
  const now = new Date();
  if (initialDate) {
    const candidate = new Date(initialDate);
    candidate.setHours(14, 0, 0, 0);
    if (candidate > now) return candidate;
  }
  return new Date(now.getTime() + 2 * 60 * 60 * 1000);
}

function startOfTomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function atTime(baseDate, hours, minutes = 0) {
  const d = new Date(baseDate);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

function buildPresets(now = new Date()) {
  const tomorrow = startOfTomorrow();
  const presets = [
    {
      id: '2h',
      label: 'In 2 hours',
      date: new Date(now.getTime() + 2 * 60 * 60 * 1000),
    },
    {
      id: 'tomorrow-2pm',
      label: 'Tomorrow 2 PM',
      date: atTime(tomorrow, 14),
    },
    {
      id: 'tomorrow-6pm',
      label: 'Tomorrow 6 PM',
      date: atTime(tomorrow, 18),
    },
  ];
  return presets.filter((preset) => preset.date > now);
}

function formatPreview(date) {
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ScheduleDateTimePicker({
  value,
  onChange,
  disabled = false,
  minDate,
  variant = 'light',
  showNowOption = false,
  publishNow = false,
  onPublishNowChange,
}) {
  const now = useMemo(() => new Date(), []);
  const effectiveMin = minDate || new Date(now.getTime() + 2 * 60 * 1000);
  const presets = useMemo(() => buildPresets(now), [now]);
  const rootClass = [
    'schedule-datetime',
    variant === 'dark' && 'schedule-datetime--dark',
  ].filter(Boolean).join(' ');

  const handlePresetClick = (preset) => {
    onPublishNowChange?.(false);
    onChange?.(preset.date);
  };

  const handleNowClick = () => {
    onPublishNowChange?.(true);
  };

  const handleDateChange = (date) => {
    onPublishNowChange?.(false);
    onChange?.(date);
  };

  return (
    <div className={rootClass}>
      <div className="schedule-datetime__presets" role="group" aria-label="Quick schedule times">
        {showNowOption && (
          <button
            type="button"
            className={[
              'schedule-datetime__preset',
              publishNow && 'is-selected',
            ].filter(Boolean).join(' ')}
            disabled={disabled}
            aria-pressed={publishNow}
            onClick={handleNowClick}
          >
            Now
          </button>
        )}
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="schedule-datetime__preset"
            disabled={disabled}
            onClick={() => handlePresetClick(preset)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {!publishNow && (
        <div className="schedule-datetime__picker-row">
          <DatePicker
            selected={value}
            onChange={handleDateChange}
            showTimeSelect
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="EEE, MMM d · h:mm aa"
            minDate={effectiveMin}
            filterTime={(time) => {
              const candidate = value ? new Date(value) : new Date();
              candidate.setHours(time.getHours(), time.getMinutes(), 0, 0);
              return candidate >= effectiveMin;
            }}
            disabled={disabled}
            placeholderText="Pick date and time"
            className="schedule-datetime__input"
            calendarClassName="schedule-datetime__calendar"
            popperClassName="schedule-datetime__popper"
            popperPlacement="bottom-start"
            showPopperArrow={false}
            shouldCloseOnSelect={false}
          />
        </div>
      )}

      {publishNow ? (
        <p className="schedule-datetime__preview">
          Posting <strong>immediately</strong>
        </p>
      ) : value && !Number.isNaN(value.getTime()) && (
        <p className="schedule-datetime__preview">
          Publishing <strong>{formatPreview(value)}</strong> your time
        </p>
      )}
    </div>
  );
}
